import { computed } from 'vue';
import type { useQuoteGeneration } from '@/composables/useQuoteGeneration';
import type { useQuoteStore } from '@/stores/quoteStore';

type QuoteGeneration = ReturnType<typeof useQuoteGeneration>;
type QuoteStore = ReturnType<typeof useQuoteStore>;

/** Dev mode: show ResultsView directly with mock data (`?dev=results` or `?view=results`). */
const readDevMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('dev') === 'results' || urlParams.get('view') === 'results';
};

export const useDevMode = () => computed(readDevMode);

const MOCK_QUOTES_RESPONSE = {
  Cobertura: {
    Id_Cia: 2,
    Nombre: "Amplia",
    PrimaTotal: 22995.55,
    PrimaTotalPrimerRecibo: 22995.55,
    PrimaTotalSubsecuentes: 0.0,
    Pagos: 1,
    ValorVehiculo: 939000,
    Coberturas: [
      {
        Id_Cobertura: 1,
        Nombre: "DAÑOS MATERIALES",
        SumaAsegurada: "939000",
        Deducible: "5%",
        EstaAmparada: true,
        PrimaNeta: 15124.91,
        PrimaTotal: 0.0,
        Show: false,
        Coaseguro: "NA",
        Observaciones: "NA"
      },
      {
        Id_Cobertura: 2,
        Nombre: "ROBO TOTAL",
        SumaAsegurada: "939000",
        Deducible: "10%",
        EstaAmparada: true,
        PrimaNeta: 12366.01,
        PrimaTotal: 0.0,
        Show: false,
        Coaseguro: "NA",
        Observaciones: "NA"
      },
      {
        Id_Cobertura: 3,
        Nombre: "RESPONSABILIDAD CIVIL",
        SumaAsegurada: "3000000",
        Deducible: "SIN DEDUCIBLE",
        EstaAmparada: true,
        PrimaNeta: 5426.38,
        PrimaTotal: 0.0,
        Show: false,
        Coaseguro: "NA",
        Observaciones: "NA"
      }
    ]
  },
  success: true
} as any;

const mockCobertura = (id: number, nombre: string, suma: string, deducible: string) => ({
  Id_Cobertura: id, Nombre: nombre, SumaAsegurada: suma, Deducible: deducible, EstaAmparada: true,
  PrimaNeta: 0, PrimaTotal: 0, Show: false, Coaseguro: "NA", Observaciones: "NA",
});

const MOCK_ANA_QUOTES_RESPONSE = {
  Cobertura: {
    Id_Cia: 3,
    Nombre: "Amplia",
    PrimaTotal: 24310.2,
    PrimaTotalPrimerRecibo: 24310.2,
    PrimaTotalSubsecuentes: 0.0,
    Pagos: 1,
    ValorVehiculo: 939000,
    Coberturas: [
      mockCobertura(10, "DAÑOS MATERIALES", "939000", "5%"),
      mockCobertura(11, "ROBO TOTAL", "939000", "10%"),
      mockCobertura(12, "RESPONSABILIDAD CIVIL", "4000000", "SIN DEDUCIBLE"),
      mockCobertura(13, "ASISTENCIA VIAL", "20000", "SIN DEDUCIBLE"),
    ]
  },
  success: true
} as any;

const seedMockVehicle = (quoteStore: QuoteStore): void => {
  quoteStore.quoteData.year = 2025;
  quoteStore.quoteData.brand = { id: "1", name: "ACURA" } as any;
  quoteStore.quoteData.type = "ADX";
  quoteStore.quoteData.model = { id: "20013", name: "20013 - ACURA - ADX - A-SPEC 5P L4 1.5T AUT., 05 OCUP." } as any;
  quoteStore.quoteData.personalData = {
    fullName: "Erik Martinez",
    email: "erik@example.com",
    whatsapp: "3327471950",
    postalCode: "2900",
    gender: "M" as const,
    birthDate: "1990-01-01"
  };
};

/** Seeds quotes and store with mock data for development. */
export const seedDevMockData = (quoteGeneration: QuoteGeneration, quoteStore: QuoteStore): void => {
  quoteGeneration.quotesResponse.value = JSON.parse(JSON.stringify(MOCK_QUOTES_RESPONSE));
  quoteGeneration.anaQuotes.value = JSON.parse(JSON.stringify(MOCK_ANA_QUOTES_RESPONSE));
  seedMockVehicle(quoteStore);
};
